import React, { useEffect, useState } from 'react';
import { Bell, Save, Check } from 'lucide-react';
import apiClient from '../api/client';

// Friendly labels for each notification type returned by the backend
const TYPE_LABELS = {
  invoice_submitted:        { label: 'Invoice submitted',         group: 'Invoices' },
  invoice_approval_needed:  { label: 'Invoice awaiting my approval', group: 'Invoices' },
  invoice_approved:         { label: 'Invoice approved (step)',    group: 'Invoices' },
  invoice_fully_approved:   { label: 'Invoice fully approved',     group: 'Invoices' },
  invoice_rejected:         { label: 'Invoice rejected',           group: 'Invoices' },
  expense_submitted:        { label: 'Expense submitted',          group: 'Expenses' },
  expense_approval_needed:  { label: 'Expense awaiting my approval', group: 'Expenses' },
  expense_approved:         { label: 'Expense approved',           group: 'Expenses' },
  expense_rejected:         { label: 'Expense rejected',           group: 'Expenses' },
};

export default function NotificationPreferences() {
  const [prefs, setPrefs] = useState({});
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const res = await apiClient.get('/notifications/preferences');
      setPrefs(res.data.preferences || {});
      setTypes(res.data.types || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load preferences');
    } finally {
      setLoading(false);
    }
  }

  function toggle(type) {
    setPrefs(prev => ({ ...prev, [type]: !prev[type] }));
  }

  async function handleSave() {
    try {
      setSaving(true);
      setError(null);
      await apiClient.put('/notifications/preferences', { preferences: prefs });
      setSavedAt(new Date());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  }

  // Group types by category
  const grouped = types.reduce((acc, type) => {
    const meta = TYPE_LABELS[type] || { label: type, group: 'Other' };
    if (!acc[meta.group]) acc[meta.group] = [];
    acc[meta.group].push({ type, label: meta.label });
    return acc;
  }, {});

  if (loading) {
    return <div className="p-8 text-gray-500">Loading preferences…</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
          <Bell className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notification Settings</h1>
          <p className="text-sm text-gray-500">Choose which email notifications you want to receive.</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
        {Object.entries(grouped).map(([group, items]) => (
          <div key={group} className="p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">{group}</h2>
            <div className="space-y-2">
              {items.map(({ type, label }) => (
                <label
                  key={type}
                  className="flex items-center justify-between gap-4 px-3 py-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <span className="text-sm text-gray-700">{label}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!prefs[type]}
                    onClick={() => toggle(type)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      prefs[type] ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        prefs[type] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {savedAt && (
            <span className="flex items-center gap-1.5 text-green-600">
              <Check className="w-4 h-4" />
              Saved at {savedAt.toLocaleTimeString()}
            </span>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors text-sm font-medium"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save preferences'}
        </button>
      </div>
    </div>
  );
}
